import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { EmptyArray } from 'lib/array/arrayUtils'
import { useIntl } from 'react-intl'
import { useExploreItems } from 'tripPlanner/hooks/api/explore'
import { useExploreOffers } from 'tripPlanner/hooks/api/exploreOffers'
import type { ChatExploreCategory } from 'tripPlanner/pages/chat/chatExploreCategories'
import type { ExploreItem } from 'tripPlanner/types/common'
import type { ExploreOffer } from 'tripPlanner/api/explore/types'
import type { MapViewState } from 'tripPlanner/components/Chat/chatMapConstants'
import { areBoundsClose, getBoundsCenter, getMaxVisibleKm, isPointInBounds, mapBoundsToUrlValue, type MapBounds } from 'tripPlanner/utils/mapBounds'
import {
  MARKER_TYPE_EXPLORE,
  MARKER_TYPE_EXPLORE_HOTEL,
  MARKER_TYPE_EXPLORE_EXPERIENCE,
  MARKER_SOURCE_EXPLORE,
  MARKER_SOURCE_EXPLORE_HOTEL,
  MARKER_SOURCE_EXPLORE_EXPERIENCE,
} from 'tripPlanner/utils/mapMarkerTransforms'
import type { MapMarkerComponent } from 'tripPlanner/utils/mapMarkerTransforms'

const BOUNDS_DEBOUNCE_MS = 400
const MAX_VISIBLE_KM = 20
const MAX_VISIBLE_KM_HIDE = 24

interface UseChatMapExploreFiltersArgs {
  mapViewState: MapViewState | undefined
  mapBounds: MapBounds | undefined
  isHotelFilterVisible: boolean
  isExperienceFilterVisible: boolean
}

export function useChatMapExploreFilters({
  mapViewState,
  mapBounds,
  isHotelFilterVisible,
  isExperienceFilterVisible,
}: UseChatMapExploreFiltersArgs) {
  const intl = useIntl()
  const [selectedCategory, setSelectedCategory] = useState<ChatExploreCategory | undefined>(undefined)
  const [appliedBounds, setAppliedBounds] = useState<MapBounds | undefined>(undefined)
  const [pendingBounds, setPendingBounds] = useState<MapBounds | undefined>(undefined)

  const [isViewportEligible, setIsViewportEligible] = useState(false)

  useEffect(() => {
    if (!mapBounds) {
      setIsViewportEligible(false)
      return
    }
    const visibleKm = getMaxVisibleKm(mapBounds)
    setIsViewportEligible(prev => {
      if (prev) {
        return visibleKm <= MAX_VISIBLE_KM_HIDE
      }
      return visibleKm <= MAX_VISIBLE_KM
    })
  }, [mapBounds])

  const isExploreFilterVisible = isViewportEligible && !isHotelFilterVisible && !isExperienceFilterVisible
  const isExploreFilterActive = isExploreFilterVisible && Boolean(selectedCategory)

  const activeSource = selectedCategory?.source
  const isGooglePlacesSource = activeSource === 'google-places'
  const isLeHotelsSource = activeSource === 'le-hotels'
  const isLeExperiencesSource = activeSource === 'le-experiences'
  const isLeOffersSource = isLeHotelsSource || isLeExperiencesSource

  const debouncedBoundsTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const skipViewportCleanupRef = useRef(false)

  useEffect(() => {
    if (!isViewportEligible) {
      if (skipViewportCleanupRef.current) return
      setSelectedCategory(undefined)
      setAppliedBounds(undefined)
      setPendingBounds(undefined)
    } else {
      skipViewportCleanupRef.current = false
    }
  }, [isViewportEligible])

  // Hotel or experience filter took priority → clear bounds but preserve category.
  // When the filter is dismissed the category will be restored and
  // fresh explore data fetched for the current viewport.
  useEffect(() => {
    if (isHotelFilterVisible || isExperienceFilterVisible) {
      setAppliedBounds(undefined)
      setPendingBounds(undefined)
    }
  }, [isHotelFilterVisible, isExperienceFilterVisible])

  useEffect(() => {
    if (!isExploreFilterVisible || !mapBounds) return undefined
    if (debouncedBoundsTimeoutRef.current) {
      clearTimeout(debouncedBoundsTimeoutRef.current)
    }
    debouncedBoundsTimeoutRef.current = setTimeout(() => {
      setPendingBounds(mapBounds)
    }, BOUNDS_DEBOUNCE_MS)
    return () => {
      if (debouncedBoundsTimeoutRef.current) {
        clearTimeout(debouncedBoundsTimeoutRef.current)
      }
    }
  }, [isExploreFilterVisible, mapBounds])

  useEffect(() => {
    if (!isExploreFilterVisible || !mapBounds) return
    if (!appliedBounds && !skipViewportCleanupRef.current) {
      setAppliedBounds(mapBounds)
      setPendingBounds(mapBounds)
    }
  }, [appliedBounds, isExploreFilterVisible, mapBounds])

  const selectCategory = useCallback((category: ChatExploreCategory | undefined, resetBounds = false) => {
    if (category) {
      skipViewportCleanupRef.current = true
    }
    if (resetBounds) {
      setAppliedBounds(undefined)
      setPendingBounds(undefined)
    }
    setSelectedCategory(category)
    if (resetBounds || !isExploreFilterVisible || !category) return
    const nextBounds = pendingBounds ?? mapBounds
    if (nextBounds) {
      setAppliedBounds(nextBounds)
      setPendingBounds(nextBounds)
    }
  }, [isExploreFilterVisible, mapBounds, pendingBounds])

  const fetchCenter = useMemo(() => {
    if (appliedBounds) {
      return getBoundsCenter(appliedBounds)
    }
    if (mapViewState) {
      return { lat: mapViewState.latitude, lng: mapViewState.longitude }
    }
    if (mapBounds) {
      return getBoundsCenter(mapBounds)
    }
    return undefined
  }, [appliedBounds, mapBounds, mapViewState])

  // --- Google Places path ---
  const exploreQuery = useMemo(() => {
    if (!isExploreFilterActive || !selectedCategory || !isGooglePlacesSource) return undefined
    if (selectedCategory.key === 'sports') {
      return intl.formatMessage({
        id: 'fMBI4PsU_syCbcXOHPll',
        defaultMessage: 'Sports venues',
        description: 'Search query term used for sports venues in the trip planner chat map explore filters',
      })
    }

    return intl.formatMessage(selectedCategory.labelMessage)
  }, [intl, isExploreFilterActive, isGooglePlacesSource, selectedCategory])

  const locationBias = useMemo(() => {
    if (!fetchCenter) return undefined
    return [fetchCenter.lat, fetchCenter.lng] as [number, number]
  }, [fetchCenter])

  const shouldFetchGooglePlaces = Boolean(exploreQuery && locationBias && isExploreFilterActive && isGooglePlacesSource)

  const {
    data: exploreItems = EmptyArray as Array<ExploreItem>,
    isLoading: exploreItemsLoading,
  } = useExploreItems(
    { query: exploreQuery, locationBias },
    { enabled: shouldFetchGooglePlaces },
  )

  // --- LE Offers path (Hotels & Experiences) ---
  const leOfferType = isLeHotelsSource ? 'hotel' : isLeExperiencesSource ? 'experience' : undefined

  const boundsUrlValue = useMemo(() => {
    if (!appliedBounds || !isLeOffersSource || !isExploreFilterActive) return undefined
    return mapBoundsToUrlValue(appliedBounds)
  }, [appliedBounds, isExploreFilterActive, isLeOffersSource])

  const {
    data: offersData,
    isLoading: offersLoading,
    isError: offersError,
    refetch: refetchOffers,
  } = useExploreOffers(
    { bounds: boundsUrlValue, offerTypes: leOfferType },
    { enabled: Boolean(boundsUrlValue) },
  )

  const exploreOffers = useMemo<Array<ExploreOffer>>(() => {
    if (!isExploreFilterActive || !isLeOffersSource || !offersData) {
      return EmptyArray as Array<ExploreOffer>
    }
    return offersData.offers
  }, [isExploreFilterActive, isLeOffersSource, offersData])

  const boundsForDisplay = appliedBounds ?? mapBounds

  const visibleExploreItems = useMemo(() => {
    if (!isExploreFilterActive || !boundsForDisplay || !isGooglePlacesSource) {
      return EmptyArray as Array<ExploreItem>
    }
    return exploreItems.filter((item) => {
      const point = item.place?.point
      if (!point) return false
      return isPointInBounds(point, boundsForDisplay)
    })
  }, [boundsForDisplay, exploreItems, isExploreFilterActive, isGooglePlacesSource])

  const googlePlacesMarkers = useMemo<Array<MapMarkerComponent>>(() => {
    if (!isExploreFilterActive || visibleExploreItems.length === 0 || !selectedCategory || !isGooglePlacesSource) {
      return EmptyArray as Array<MapMarkerComponent>
    }
    // visibleExploreItems is pre-filtered to items with place.point,
    // but we use optional chaining defensively in case the data mutates.
    return visibleExploreItems.flatMap((item) => {
      const point = item.place?.point
      if (!point) return []
      return [{
        lat: point.lat,
        lng: point.lng,
        label: item.name,
        description: item.editorialSummary ?? undefined,
        placeId: item.id,
        rating: item.rating,
        userRatingCount: item.userRatingCount,
        photoName: item.photo?.name,
        markerType: MARKER_TYPE_EXPLORE,
        categoryKey: selectedCategory.key,
        source: MARKER_SOURCE_EXPLORE,
      }]
    })
  }, [isExploreFilterActive, isGooglePlacesSource, selectedCategory, visibleExploreItems])

  const leHotelMarkers = useMemo<Array<MapMarkerComponent>>(() => {
    if (!isExploreFilterActive || !isLeHotelsSource || exploreOffers.length === 0 || !selectedCategory) {
      return EmptyArray as Array<MapMarkerComponent>
    }
    return exploreOffers.map((offer) => ({
      lat: offer.coordinates.lat,
      lng: offer.coordinates.lng,
      label: offer.name,
      markerId: offer.id,
      offerId: offer.id,
      imageId: offer.imageIds[0],
      offerPrice: offer.price,
      markerType: MARKER_TYPE_EXPLORE_HOTEL,
      categoryKey: selectedCategory.key,
      source: MARKER_SOURCE_EXPLORE_HOTEL,
    }))
  }, [exploreOffers, isExploreFilterActive, isLeHotelsSource, selectedCategory])

  const leExperienceMarkers = useMemo<Array<MapMarkerComponent>>(() => {
    if (!isExploreFilterActive || !isLeExperiencesSource || exploreOffers.length === 0 || !selectedCategory) {
      return EmptyArray as Array<MapMarkerComponent>
    }
    return exploreOffers.map((offer) => ({
      lat: offer.coordinates.lat,
      lng: offer.coordinates.lng,
      label: offer.name,
      markerId: offer.id,
      offerId: offer.id,
      imageId: offer.imageIds[0],
      offerPrice: offer.price,
      markerType: MARKER_TYPE_EXPLORE_EXPERIENCE,
      categoryKey: selectedCategory.key,
      source: MARKER_SOURCE_EXPLORE_EXPERIENCE,
    }))
  }, [exploreOffers, isExploreFilterActive, isLeExperiencesSource, selectedCategory])

  const exploreMarkers = isLeHotelsSource
    ? leHotelMarkers
    : isLeExperiencesSource
      ? leExperienceMarkers
      : googlePlacesMarkers

  const hasExploreResults = isExploreFilterActive && exploreMarkers.length > 0

  const isExploreLoading = isLeOffersSource
    ? Boolean(boundsUrlValue) && offersLoading
    : shouldFetchGooglePlaces && exploreItemsLoading

  const isBoundsDirty = useMemo(() => {
    if (!isExploreFilterActive || !pendingBounds || !appliedBounds) {
      return false
    }
    return !areBoundsClose(pendingBounds, appliedBounds)
  }, [appliedBounds, isExploreFilterActive, pendingBounds])

  const applyBounds = useCallback(() => {
    const nextBounds = pendingBounds ?? mapBounds
    if (!nextBounds) return
    setAppliedBounds(nextBounds)
    setPendingBounds(nextBounds)
  }, [mapBounds, pendingBounds])

  // Clear all explore filter state (used on chat switch / new chat).
  // Cancels any pending debounce to prevent stale state after reset.
  const reset = useCallback(() => {
    setSelectedCategory(undefined)
    setAppliedBounds(undefined)
    setPendingBounds(undefined)
    skipViewportCleanupRef.current = false
    if (debouncedBoundsTimeoutRef.current) {
      clearTimeout(debouncedBoundsTimeoutRef.current)
      debouncedBoundsTimeoutRef.current = undefined
    }
  }, [])

  return {
    selectedCategory,
    selectCategory,
    exploreMarkers,
    exploreOffers,
    isViewportEligible,
    isExploreFilterVisible,
    isExploreFilterActive,
    isExploreLoading,
    isExploreOffersError: isLeOffersSource && offersError,
    refetchExploreOffers: refetchOffers,
    hasExploreResults,
    isBoundsDirty,
    applyBounds,
    reset,
  }
}
